import React, {useEffect} from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {SubTitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import css from './booking-reschedule-form.css';
import {ReasonInput} from '../reason-form/reason-input';
import {zodResolver} from '@hookform/resolvers/zod';
import {getReasonFormSchema} from '../reason-form/reason-form.schema';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {useBookingMachine} from '../../hooks/useBookingMachine';

import {useSelector} from '@xstate/react';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {selectError} from '../../selectors/commonSelector';
import {selectSelectedTimeSlot} from '../../selectors/booking-selector';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {TEST_ID} from '../../utils/testId';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';


export const BookingRescheduleForm = () => {
  const {t} = useLabelTranslation();

  const {bookingActor, bookingState} = useBookingMachine();
  const selectedTimeSlot = useSelector(bookingActor, selectSelectedTimeSlot);
  const {errorMessage} = useSelector(bookingActor, (state) =>
    selectError(state, t),
  );

  const hasError = bookingState.matches({
    rescheduleForm: 'retryRescheduleFormSubmit',
  });

  const reasonSchema = getReasonFormSchema(t);
  const methods = useForm<z.infer<typeof reasonSchema>>({
    mode: 'onTouched',
    defaultValues: {
      reason: '',
    },
    resolver: zodResolver(reasonSchema),
  });

  const {control, formState, watch} = methods;

  const reasonValue = watch('reason');
  const handleRescheduleMeeting = () => {
    if (selectedTimeSlot) {
      bookingActor.send({type: 'timeslot.confirm', timeSlot: selectedTimeSlot});
    }
  };

  const handleBack = () => {
    bookingActor.send({type: 'rescheduleReasonForm.back'});
  };

  const isRescheduleFormSubmitting = bookingState.matches({
    rescheduleForm: 'rescheduleFormSubmitting',
  });

  useEffect(() => {
    bookingActor.send({
      type: 'rescheduleReason.update',
      reason: reasonValue,
    });
  }, [reasonValue]);

  return (
    <>
      <div className={css.rescheduleForm}>
        <SubTitleMedium color="primary">
          {t(
            'Please_answer_the_following_questions',
            'Please answer the following questions ',
          )}
        </SubTitleMedium>
        <ReasonInput
          control={control}
          reasonName="reason"
          required={true}
          disabled={false}
          actionType="reschedule"
          participantType="CANDIDATE"
          testId={generateTestId({
            base: TEST_ID.BOOKING_FLOW,
            slot: 'reschedule-reason-input',
          })}
        />
      </div>
      <div className={css.rescheduleButtonSection}>
        <div className={css.buttonContainer}>
          <Button
            type="tertiary"
            size="medium"
            iconLeftName="chevron-left"
            onClick={handleBack}
            disabled={isRescheduleFormSubmitting}
            testId={generateTestId({
              base: TEST_ID.BOOKING_FLOW,
              slot: 'reschedule-back-button',
            })}
          >
            {t('Back', 'Back')}
          </Button>
          <BrandButton
            size="medium"
            actionType="submit"
            disabled={!selectedTimeSlot || !formState.isValid}
            isLoading={bookingState.matches({
              rescheduleForm: 'rescheduleFormSubmitting',
            })}
            onClick={handleRescheduleMeeting}
            testId={generateTestId({
              base: TEST_ID.BOOKING_FLOW,
              slot: 'reschedule-meeting-button',
            })}
          >
            {t('Reschedule_Meeting', 'Reschedule Meeting')}
          </BrandButton>
        </div>
        {hasError && (
          <InContextAlert
            leftIconName="shield-exclamation"
            semantic="danger"
            testId={generateTestId({
              base: TEST_ID.BOOKING_FLOW,
              slot: 'reschedule-in-context-alert-error',
            })}
          >
            {/* already translated in errorMessage */}
            {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
            {errorMessage}
          </InContextAlert>
        )}
      </div>
    </>
  );
};
